import React, {
  useState,
  useEffect
} from 'react';
import moment from 'moment';

import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Chip,
  Grid,
  Typography,
  Button,
} from '@material-ui/core/';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';

import {
  ProfileChip,
  StatusChip,
  DateTimeView,
  UserComment,
  EventItem,
  DocumentView
} from '../../general';

import localization from '../../../utils/localizations';
import lastStatus from '../../../utils/lastStatus';
import {
  Document,
  Status,
  Room,
  Schedule,
  ThesisProposal,
} from '../../../types';


const useStyles = makeStyles((theme) => ({
  mainBody: {
    padding: 10,
  },
  mainGrid: {
    marginTop: 20,
  },
  gridLabelBolder: {
    textAlign: 'right',
    fontWeight: 500,
  },
  profileSpacing: {
    marginRight: 10,
  },
  marginVertical: {
    marginTop: 10,
    marginBottom: 10
  }
}));


function ThesisProposalDetail(props: {proposal: ThesisProposal}) {
  const classes = useStyles();
  let lastSubmittedStatus = lastStatus(props.proposal.status_history, 'submitted');
  let lastUpdatedStatus = lastStatus(props.proposal.status_history, props.proposal.status);
  return (
    <div>
      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>{localization.title}</Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          <Typography variant="body1">{props.proposal.title}</Typography>

          {props.proposal.advisor_research &&
            <div>
              <Chip
                size="small"
                label={localization.advisorResearch}
                color="primary"
                className={classes.marginVertical}
              />
            </div>}

        </Grid>
      </Grid>

      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>{localization.proposalStatus}</Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          <StatusChip status={props.proposal.status} size="small" namespace="proposal" />
          {lastUpdatedStatus?.comment &&
          <div>
            <Typography variant="subtitle2" className={classes.marginVertical}>{localization.comment}</Typography>
            <UserComment
              comment={lastUpdatedStatus.comment}
              inline
            />
          </div>
          }
        </Grid>
      </Grid>

      {lastSubmittedStatus &&
      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>{localization.proposalSubmittedAt}</Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          <Typography variant="body1"><DateTimeView value={lastSubmittedStatus.updated_at} chip /></Typography>
        </Grid>
      </Grid>
      }

      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>{localization.prospectivePrimaryAdvisor}</Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          {props.proposal.primary_advisor &&
            <ProfileChip profile={props.proposal.primary_advisor} />
          }
        </Grid>
      </Grid>

      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>{localization.additionalProspectiveAdvisors}</Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          {props.proposal.additional_advisors.map((advisor, i) => {
            return <ProfileChip key={`advisor-${i}`} profile={advisor} className={classes.profileSpacing} />
          })}
        </Grid>
      </Grid>

    {props.proposal.documents.map((doc: Document, j: number) => {
        let label = (localization as any)[doc.kind] ? (localization as any)[doc.kind] : doc.kind;
        let prevDoc = (j > 0) ? props.proposal.documents[j-1] : undefined;
        if (prevDoc && prevDoc.kind == doc.kind) label = '';
        return (
          <Grid container spacing={3} className={classes.mainGrid}>
            <Grid item xs={4} sm={3}>
              <Typography variant="body1" className={classes.gridLabelBolder}>{label}</Typography>
            </Grid>
            <Grid item xs={8} sm={9}>
              <DocumentView doc={doc} showSimilarity={doc.kind === 'Proposal'} />
            </Grid>
          </Grid>
        );
      })}

      {(props.proposal.schedules.length > 0) &&
        <Grid container spacing={3} className={classes.mainGrid}>
          <Grid item xs={4} sm={3}>
            <Typography variant="body1" className={classes.gridLabelBolder}>{localization.schedule}</Typography>
          </Grid>
          <Grid item xs={8} sm={9}>
          {props.proposal.schedules.map((event: Schedule, j: number) => {
            return (
              <EventItem
                key={`schedule-event-${j}`}
                event={event}
              />
            );
          })}
          </Grid>
        </Grid>
      }
    </div>
    );
}

export {ThesisProposalDetail};